import {explorationSteps} from "../helpers/explorationGuide";
import MetricGuideControls from "./MetricGuideControls";
import MetricExplanation from "./MetricExplanation";
import FocusedStatisticGuide from "./FocusedStatisticGuide";
import React, { useMemo, useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
} from "@mui/material";
import { guidedAnalysis, guidedQuestions } from "../helpers/guidedTeamAnalysis";
import { strengthRanking } from "../helpers/teamSettingsImpact";
import { goalFieldLabel } from "../helpers/teamSettingValues";
import TeamModelReferences from "./TeamModelReferences";
import TeamReassessment from "./TeamReassessment";
import TeamProposalWorkbench from "./TeamProposalWorkbench";
import TeamLeagueInline from "./TeamLeagueInline";
import "./TeamGuidedAnalysis.css";

export default function TeamGuidedAnalysis({
  exploration = false,
  guideSession,
  data,
  teamId,
  en,
  mode,
  names,
  logo,
  league,
  statName,
  draft,
  ranking,
  stat,
  dirty,
  busy,
  onDetails,
  onEdit,
  onApplyResult,
  onApplyStat,
  onPreviewStat,
  onPreviewReassessment,
  onApplyReassessment,
  onDrawImpact,
  onReview,
  renderImpact,
  previewOpen,
}) {
  const t = (d, e) => (en ? e : d),
    fmt = (n, d = 3) =>
      Number.isFinite(n)
        ? n.toLocaleString(en ? "en-GB" : "de-DE", {
            minimumFractionDigits: d,
            maximumFractionDigits: d,
          })
        : "—";
  const [question, setQuestion] = useState("strength"),
    [model, setModel] = useState("result"),
    [strengthSide, setStrengthSide] = useState("ratio"),
    [strengthVenue, setStrengthVenue] = useState("total"),
    [recent, setRecent] = useState(8),
    [statField, setStatField] = useState("for_total"),
    [overlay, setOverlay] = useState(null),
    [rankingDraft, setRankingDraft] = useState(false),
    [decisions, setDecisions] = useState({});
  const [session,setSession]=useState(()=>guideSession?.[teamId]||{active:exploration,index:0,mode:'explain',playing:false,compact:false});
  useEffect(()=>{if(guideSession)guideSession[teamId]={...session,playing:false};},[session,guideSession,teamId]);
  const metricSteps=useMemo(()=>explorationSteps(data.state,en).map(s=>({...s,title:s.statistic&&s.statistic!=='conversion'?statName(s.statistic):s.title})),[data.state,en,statName]);
  const guideStep=metricSteps[Math.min(session.index,metricSteps.length-1)];
  const focused=exploration&&session.active;
  const explain=focused&&session.mode==='explain';
  useEffect(()=>{if(!focused||!guideStep)return;setModel(guideStep.model);setQuestion(guideStep.question);setStrengthSide(guideStep.field?.split('_')[0]||'ratio');setStrengthVenue(guideStep.field?.split('_')[1]||'total');},[focused,guideStep.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const evidence = useMemo(
    () => guidedAnalysis(data, teamId, { en, recent, statField, statName }),
    [data, teamId, en, recent, statField, statName]
  );
  const focusField =
    question === "strength" && strengthSide !== "ratio"
      ? `${strengthSide}_${strengthVenue}`
      : null;
  const taskLabel = focusField
    ? en
      ? goalFieldLabel(focusField, en)
      : goalFieldLabel(focusField, en).replace(
          /^(Offensive|Defensive) (Heim|Auswärts)$/,
          "$2 $1"
        )
    : guidedQuestions.find((q) => q[0] === question)?.[en ? 2 : 1] || "";
  const steps = evidence.sections[question] || [];
  const reviewRef = React.useRef(onReview);
  reviewRef.current = onReview;
  useEffect(() => {
    reviewRef.current(true);
  }, []);
  useEffect(() => {
    setDecisions({});
  }, [data.result_revision]);
  const rankingGroups = [
    {key:"result", title:t("Ergebnismodell", "Results model"), rows:strengthRanking(rankingDraft && dirty ? draft : data.result_teams)},
    {key:"statistics", title:t("Statistikmodell", "Statistics model"), rows:strengthRanking(Object.fromEntries((rankingDraft && dirty ? ranking : data.ranking || []).map(r=>[r.team_id,r])))}
  ];
  const fieldNames = {
    home_for: t("Heim · Für", "Home · for"),
    home_against: t("Heim · Gegen", "Home · against"),
    away_for: t("Auswärts · Für", "Away · for"),
    away_against: t("Auswärts · Gegen", "Away · against"),
    for_total: t("Gesamt · Für", "Overall · for"),
    against_total: t("Gesamt · Gegen", "Overall · against"),
  };
  return (
    <section
      className="tg-analysis"
      aria-label={t("Geführte Team-Analyse", "Guided team analysis")}
    >
      <nav
        className="tg-context"
        aria-label={t("Kontext öffnen", "Open context")}
      >
        <Button onClick={() => setOverlay("table")}>
          {t("Aktuelle Ligatabelle", "Current league table")}
        </Button>
        <Button onClick={() => {setRankingDraft(!!dirty);setOverlay("ranking");}}>
          {t("Spielstärke-Ranking der Liga", "League strength ranking")}
        </Button>
        <Button onClick={() => setOverlay("parameters")}>
          {t(
            "Gespeicherte Teamwerte & Season Parameters",
            "Saved team values & Season Parameters"
          )}
        </Button>
        <Button onClick={onDetails}>
          {t("Alle Zahlen & Details", "All numbers & details")}
        </Button>
      </nav>
      <div className="tg-edit-context" aria-live="polite">
        <div className="tg-edit-team">
          {logo(teamId)}{" "}
          {league?.country?.image_path && (
            <img
              className="tg-country"
              src={league.country.image_path}
              alt={league.country.name || ""}
            />
          )}{" "}
          <strong>{names(teamId)}</strong>
        </div>
        <div>
          <strong>
            {model === "result"
              ? t("Ergebnisbasiertes Modell", "Results model")
              : t("Statistikbasiertes Modell", "Statistics model")}
          </strong>
          <div>
            {focused?guideStep.title:taskLabel} {explain?t("verstehen", "· understand"):t("bearbeiten", "· edit")}
          </div>
        </div>
      </div>
      {exploration&&<MetricGuideControls steps={metricSteps} session={session} onChange={setSession} en={en}/>}
      {focused&&!guideStep.statistic&&<h2>{guideStep.title}</h2>}
      {focused&&<label>{t("Jüngste Spiele","Recent matches")} <select aria-label={t("Jüngste Spiele","Recent matches")} value={recent} onChange={e=>setRecent(Number(e.target.value))}><option value={6}>6</option><option value={8}>8</option></select></label>}
      <div style={{display:focused?'none':undefined}}>
      <h2>
        {t(
          "Was möchtest du untersuchen?",
          "What would you like to investigate?"
        )}
      </h2>
      <div className="tw-models">
        {["result", "statistics"].map((m) => (
          <fieldset key={m} className={model === m ? "tw-model-active" : ""}>
            <legend>
              {m === "result"
                ? t(
                    "Ergebnisbasiertes Modell · führendes Modell",
                    "Results model · leading model"
                  )
                : t(
                    "Statistikbasiertes Modell · unabhängige Einschätzung",
                    "Statistics model · independent assessment"
                  )}
            </legend>
            <div className="tg-questions">
              {guidedQuestions
                .filter(([q]) => m === "statistics" || q !== "statistics")
                .map(([q, de, eng]) => (
                  <Button
                    key={q}
                    variant={
                      model === m && question === q ? "contained" : "outlined"
                    }
                    aria-pressed={model === m && question === q}
                    onClick={() => {
                      setModel(m);
                      setQuestion(q);
                    }}
                  >
                    {en ? eng : de}
                  </Button>
                ))}
            </div>
          </fieldset>
        ))}
      </div>
      {question === "strength" && (
        <div
          className="tg-strength-focus"
          aria-label={t("Spielstärke bearbeiten", "Edit strength")}
        >
          <div>
            {[
              ["ratio", "Spielstärke · Quotient", "Strength · ratio"],
              ["for", "Offensive", "Attack"],
              ["against", "Defensive", "Defence"],
            ].map(([key, de, eng]) => (
              <Button
                key={key}
                variant={strengthSide === key ? "contained" : "outlined"}
                aria-pressed={strengthSide === key}
                onClick={() => setStrengthSide(key)}
              >
                {en ? eng : de}
              </Button>
            ))}
          </div>
          {strengthSide !== "ratio" && (
            <div>
              {[
                ["total", "Gesamt", "Overall"],
                ["home", "Heim", "Home"],
                ["away", "Auswärts", "Away"],
              ].map(([key, de, eng]) => (
                <Button
                  key={key}
                  variant={strengthVenue === key ? "contained" : "outlined"}
                  aria-pressed={strengthVenue === key}
                  onClick={() => setStrengthVenue(key)}
                >
                  {en ? eng : de}{" "}
                  {strengthSide === "for"
                    ? t("Offensive", "attack")
                    : t("Defensive", "defence")}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="tg-controls">
        <label>
          {t("Jüngste Spiele", "Recent matches")}{" "}
          <select
            aria-label={t("Jüngste Spiele", "Recent matches")}
            value={recent}
            onChange={(e) => setRecent(+e.target.value)}
          >
            {[4, 5, 6, 7, 8].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        {question === "statistics" && (
          <label>
            {t("Statistikwerte für", "Statistic values for")}{" "}
            <select
              value={statField}
              onChange={(e) => setStatField(e.target.value)}
            >
              {Object.entries(fieldNames).map(([f, n]) => (
                <option key={f} value={f}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      </div>
      {!focused&&<TeamReassessment
        {...{ data, teamId, en, mode, names, logo, statName, busy, recent }}
        onPreview={onPreviewReassessment}
        onApply={onApplyReassessment}
        onDrawImpact={onDrawImpact}
      />}
      {focused&&guideStep.statistic?<FocusedStatisticGuide key={guideStep.id} {...{data,teamId,en,explain,statName,stat,onPreviewStat,onApplyStat}} statistic={guideStep.statistic} compact={session.compact} busy={busy}/>:
      explain?<MetricExplanation {...{data,teamId,model,question,recent,en,steps}} field={focusField} compact={session.compact} onInspect={()=>setSession({...session,mode:"edit",playing:false})}/>:
      <TeamProposalWorkbench
        focusField={focusField}
        onFocusField={(f) => {
          if (/^(for|against)_(total|home|away)$/.test(f)) {
            setQuestion("strength");
            setStrengthSide(f.split("_")[0]);
            setStrengthVenue(f.split("_")[1]);
          }
        }}
        {...{
          data,
          teamId,
          model,
          question,
          recent,
          en,
          mode,
          steps,
          draft,
          ranking,
          stat,
          busy,
          onApplyResult,
          onApplyStat,
          onPreviewStat,
          onPreviewReassessment,
          onApplyReassessment,
        }}
      />
      }
      {!focused&&question === "statistics" && (
        <section className="tg-review">
          <Button variant="contained" onClick={onEdit}>
            {t("Einzelstatistiken bearbeiten", "Edit individual statistics")}
          </Button>
          {evidence.proposals.map((p) => (
            <Proposal
              key={p.key}
              {...{ p, t, fmt, busy }}
              onPreview={(value) => onPreviewStat({ ...p, value })}
              decision={decisions[p.key]}
              onReject={() =>
                setDecisions((d) => ({ ...d, [p.key]: "rejected" }))
              }
              onApply={async (value) => {
                const ok = await onApplyStat({ ...p, value });
                if (ok !== false)
                  setDecisions((d) => ({ ...d, [p.key]: "applied" }));
              }}
            />
          ))}
          {!evidence.proposals.length && (
            <Alert severity="info">
              {t(
                "Keine begründbaren automatischen Einzelvorschläge aus den verfügbaren Vor-Spiel-Erwartungen.",
                "No supported automatic proposals from the available pre-match expectations."
              )}
            </Alert>
          )}
          <details>
            <summary>
              {t(
                "Conversion Rate selbst ändern · kein automatischer Vorschlag",
                "Edit conversion rate manually · no automatic proposal"
              )}
            </summary>
            <p>{evidence.conclusions.statistics.at(-1)}</p>
            {Object.entries(fieldNames)
              .filter(([f]) => !f.endsWith("total"))
              .map(([field, label]) => (
                <ManualValue
                  key={field}
                  {...{ t, busy }}
                  label={`Conversion · ${label} (%)`}
                  value={
                    Number.isFinite(stat?.teams?.[teamId]?.conversion?.[field])
                      ? 100 * stat.teams[teamId].conversion[field]
                      : null
                  }
                  onApply={(value) =>
                    onApplyStat({
                      group: "conversion",
                      team_id: Number(teamId),
                      field,
                      value: value / 100,
                    })
                  }
                />
              ))}
          </details>
          {onEdit && (
            <Button onClick={onEdit}>
              {t(
                "Alle Einzelstatistiken selbst bearbeiten",
                "Edit all individual statistics"
              )}
            </Button>
          )}
        </section>
      )}
      {dirty && (
        <details className="tw-impact" open={previewOpen || undefined}>
          <summary>
            {t(
              "Auswirkungen des Entwurfs · beide Modelle, Liga und Ranking",
              "Draft effects · both models, league and ranking"
            )}
          </summary>
          {renderImpact()}
        </details>
      )}
      <Dialog
        open={!!overlay}
        onClose={() => setOverlay(null)}
        maxWidth="lg"
        fullWidth
        sx={{ zIndex: 1700 }}
        PaperProps={{ className: `team-analysis ta-${mode}` }}
      >
        <DialogTitle className="tw-dialog-title">
          <span>{logo(teamId)} {names(teamId)} · {league?.name} · {data.season?.name}</span>
          {overlay === "parameters"
            ? "Season Parameters"
            : overlay === "table"
            ? t("Aktuelle Ligatabelle", "Current league table")
            : t("Spielstärke-Ranking der Liga", "League strength ranking")}
          <Button onClick={() => setOverlay(null)}>
            {t("Schließen", "Close")}
          </Button>
        </DialogTitle>
        <DialogContent>
          {overlay === "parameters" ? (
            <TeamModelReferences data={data} teamIds={[teamId]} en={en} />
          ) : overlay === "table" ? (
            <TeamLeagueInline
              {...{ data, en, mode }}
              teamId={teamId}
              actualOnly
            />
          ) : overlay === "ranking" ? (
            <>
              <label>
                {t("Zustand", "State")}{" "}
                <select
                  aria-label={t("Zustand", "State")}
                  value={rankingDraft ? "draft" : "saved"}
                  onChange={(e) => setRankingDraft(e.target.value === "draft")}
                >
                  <option value="saved">
                    {t("Aktuell gespeichert", "Currently saved")}
                  </option>
                  <option value="draft" disabled={!dirty}>
                    {t("Entwurf · nicht gespeichert", "Draft · not saved")}
                  </option>
                </select>
              </label>
              <div className="tg-ranking-pair">{rankingGroups.map(({key,title,rows})=><section key={key} aria-label={title}>
              <h3>{title}</h3><div className="ta-scroll">
                <table className="tg-ranking" aria-label={title}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t("Mannschaft", "Team")}</th>
                      <th>{t("Spielstärke · Quotient", "Strength · ratio")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.id}
                        className={
                          Number(r.id) === Number(teamId) ? "tg-selected" : ""
                        }
                      >
                        <td>{r.rank}</td>
                        <td>
                          {logo(r.id)} {names(r.id)}
                        </td>
                        <td>{fmt(r.total_strength)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!rows.length && (
                <p>
                  {t(
                    "Für dieses Modell fehlen gespeicherte Teamwerte.",
                    "Saved team values are missing for this model."
                  )}
                </p>
              )}
              </section>)}</div>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          {overlay === "parameters" && onEdit && (
            <Button
              disabled={data.read_only}
              onClick={() => {
                setOverlay(null);
                onEdit();
              }}
            >
              {t("Gespeicherte Teamwerte bearbeiten", "Edit saved team values")}
            </Button>
          )}
          <Button onClick={() => setOverlay(null)}>
            {t("Zur Untersuchung zurück", "Return to investigation")}
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}
function ManualValue({ label, value, onApply, t, busy }) {
  const [input, setInput] = useState("");
  useEffect(
    () =>
      setInput(Number.isFinite(value) ? String(Number(value.toFixed(3))) : ""),
    [value]
  );
  return (
    <div className="tg-manual">
      <TextField
        size="small"
        type="number"
        label={label}
        value={input}
        disabled={busy || !Number.isFinite(value)}
        onChange={(e) => setInput(e.target.value)}
        inputProps={{ step: 0.001 }}
      />
      <Button
        disabled={
          busy ||
          input === "" ||
          !Number.isFinite(Number(input)) ||
          Number(input) <= 0
        }
        onClick={() => onApply(Number(input))}
      >
        {t("Als Entwurf übernehmen", "Apply as draft")}
      </Button>
    </div>
  );
}
function Proposal({ p, t, fmt, busy, decision, onReject, onApply, onPreview }) {
  const [manual, setManual] = useState(false),
    [value, setValue] = useState(
      String(Number((p.value * (p.percent ? 100 : 1)).toFixed(3)))
    );
  const [impact, setImpact] = useState(null),
    [pending, setPending] = useState(false),
    [error, setError] = useState("");
  const scale = p.percent ? 100 : 1,
    target = manual ? Number(value) / scale : p.value;
  const valid =
    Number.isFinite(target) && target >= 0 && (!manual || value !== "");
  const inspect = async () => {
    setPending(true);
    setError("");
    try {
      setImpact({ ...(await onPreview(target)), target });
    } catch (e) {
      setError(e.message);
    } finally {
      setPending(false);
    }
  };
  return (
    <article className="tg-proposal">
      <h3>{p.title}</h3>
      <p>
        {t("Gespeichert", "Saved")} <b>{fmt(p.before * scale)}</b> →{" "}
        {t("Vorschlag", "Proposal")} <b>{fmt(p.value * scale)}</b>
        {p.percent ? " %" : ""}
      </p>
      <p>{p.reason}</p>
      {decision ? (
        <p role="status">
          {decision === "applied"
            ? t(
                "Im Entwurf. Auswirkungen unten prüfen; noch nicht gespeichert.",
                "In draft. Inspect effects below; not saved yet."
              )
            : t("Nicht übernommen.", "Not applied.")}
        </p>
      ) : (
        <>
          <Button
            disabled={busy || pending}
            onClick={() => {
              setManual((v) => !v);
              setImpact(null);
            }}
          >
            {t("Selbst ändern", "Edit manually")}
          </Button>
          <Button disabled={busy || pending} onClick={onReject}>
            {t("Nicht ändern", "Keep unchanged")}
          </Button>
          {manual && (
            <TextField
              type="number"
              size="small"
              label={t("Eigener Zielwert", "Your target")}
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setImpact(null);
              }}
            />
          )}
          <Button disabled={busy || pending || !valid} onClick={inspect}>
            {t("Auswirkung prüfen", "Inspect effect")}
          </Button>
          {error && <Alert severity="error">{error}</Alert>}
          {impact && (
            <p className="tg-candidate-effect">
              {t(
                "Statistikmodell · bestehender Entwurf → mit dieser Änderung",
                "Statistics model · existing draft → with this change"
              )}
              : {t("Spielstärke", "Strength")}{" "}
              {fmt(impact.before?.total_strength)} →{" "}
              {fmt(impact.after?.total_strength)};{" "}
              {t("Torniveau", "Goal level")} {fmt(impact.before?.total_goals)} →{" "}
              {fmt(impact.after?.total_goals)}; {t("Rang", "Rank")}{" "}
              {impact.before?.rank ?? "—"} → {impact.after?.rank ?? "—"}.{" "}
              {t(
                "Noch nicht übernommen oder gespeichert.",
                "Not yet applied or saved."
              )}
            </p>
          )}
          <Button
            disabled={busy || pending || !impact || impact.target !== target}
            onClick={() => onApply(target)}
          >
            {t("Übernehmen", "Apply")}
          </Button>
        </>
      )}
    </article>
  );
}
